import { BsExclamationCircleFill } from 'react-icons/bs';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import Button from '../common/Button';
import { useSignInWithProvider, useSignUp } from '../../core/account/AuthServiceHooks';
import Yup, { useYupValidationResolver } from '../../core/helpers/yup';
import FormInput from '../common/FormInput';
import FormPasswordInput from '../common/FormPasswordInput';
import {
  AuthenticationStep,
  CognitoAuthProvider,
  CustomAuthProvider,
  Locale,
  SocialProviderSequence,
} from '../../core/constants';
import Link from '../common/Link/Link';

const AppleLoginConfirmationModal = dynamic(() => import('./AppleLoginConfirmationModal'));

type FormValues = {
  familyName: string;
  givenName: string;
  email: string;
  password: string;
};

type Props = {
  showTitle?: boolean;
  showInModal?: boolean;
  onSignUpSuccess?: (email: string) => void;
};

const SignUpCard = ({ showTitle = true, showInModal, onSignUpSuccess }: Props) => {
  const { locale } = useRouter();
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation(['common', 'component']);
  const [signUp, loading, code, message] = useSignUp();
  const router = useRouter();
  const redirectPath =
    (
      router.query as {
        redirect?: string;
      }
    ).redirect || router.asPath;
  const [signInWithProvider] = useSignInWithProvider(AuthenticationStep.SIGN_UP, redirectPath);

  const schema = Yup.object({
    familyName: Yup.customer()
      .name()
      .label(t('component:sign_up_card.label.family_name'))
      .required(),
    givenName: Yup.customer().name().label(t('component:sign_up_card.label.given_name')).required(),
    email: Yup.customer()
      .email({ mobile: 'email.sign_in_mobile_carrier' })
      .label(t('component:sign_up_card.label.email'))
      .required(),
    password: Yup.credential()
      .password({
        min: 'password.minimum_length',
        alphanumeric: 'password.lowercase_uppercase_number_special_character',
        whitespace: 'password.no_whitespace',
      })
      .label(t('component:sign_up_card.label.password'))
      .required(),
  });

  const { handleSubmit, control, formState, setError } = useForm<FormValues>({
    defaultValues: {
      familyName: '',
      givenName: '',
      email: '',
      password: '',
    },
    mode: 'all',
    resolver: useYupValidationResolver(schema),
  });

  const events = {
    handleSubmit: async (data: FormValues) => {
      await signUp(data.givenName, data.familyName, data.email, data.password, onSignUpSuccess);
    },
    onClickProviderLogin: async (provider: CognitoAuthProvider | CustomAuthProvider) => {
      if (
        process.env.NEXT_PUBLIC_FEATURE_FLAG_SSO_APPLE_CONFIRMATION_MODAL === 'true' &&
        provider === CognitoAuthProvider.APPLE &&
        !showModal
      ) {
        setShowModal(true);
      } else {
        await signInWithProvider(provider);
      }
    },
  };

  useEffect(() => {
    if (message && !loading) {
      if (code === 'InvalidPasswordException') {
        setError('password', { types: { custom: message } });
      } else if (code === 'UsernameExistsException' || code === 'AliasExistsException') {
        setError('email', { types: { custom: message } });
      }
    }
  }, [message, loading, setError, code]);

  return (
    <>
      <div className="relative flex flex-col items-center">
        {showTitle && (
          <div className="w-full text-left text-large font-bold">
            {t('component:sign_up_card.title')}
          </div>
        )}
        <form className="w-full" onSubmit={handleSubmit(events.handleSubmit)}>
          <div className={classNames('flex flex-col w-full', { 'mt-8': showTitle })}>
            {/* family name and given name */}
            <p className="mb-2 text-small font-bold">
              {t('component:sign_up_card.label.name.title')}
              <span className="text-mini font-normal">
                {t('component:sign_up_card.label.name.kanji_alphabet')}
              </span>
            </p>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:gap-4">
              <FormInput
                className="w-full"
                background="gray"
                border={false}
                autoComplete="family-name"
                size="large"
                placeholder={t('component:sign_up_card.placeholder.family_name')}
                control={control}
                name="familyName"
              />
              <FormInput
                className="mt-2 w-full sm:mt-0"
                background="gray"
                border={false}
                autoComplete="given-name"
                size="large"
                placeholder={t('component:sign_up_card.placeholder.given_name')}
                control={control}
                name="givenName"
              />
            </div>
          </div>

          <div className="mt-4 flex w-full flex-col">
            <FormInput
              background="gray"
              border={false}
              type="text"
              autoComplete="email"
              size="large"
              showError
              label={t('component:sign_up_card.label.email')}
              labelClassName="font-bold"
              placeholder={t('component:sign_up_card.placeholder.email')}
              control={control}
              name="email"
              requirements={['email.sign_in_mobile_carrier']}
            />
          </div>

          <div className="relative mt-6 flex w-full flex-col">
            <FormPasswordInput
              newPassword
              label={t('component:sign_up_card.label.password')}
              background="gray"
              border={false}
              size="large"
              placeholder={t('component:sign_up_card.placeholder.password')}
              control={control}
              showError
            />
          </div>
          {Object.keys(formState.errors).length > 0 &&
            message !== formState.errors.email?.types?.custom &&
            message !== formState.errors.password?.types?.custom && (
              <p className="flex items-center justify-center text-small text-orange-700">
                <BsExclamationCircleFill className="mr-1" />
                {message}
              </p>
            )}
          <Button
            className="my-4 w-full font-bold"
            size="medium"
            color="red"
            type="submit"
            disabled={Object.keys(formState.errors).length > 0}
            loading={loading}
          >
            {t('button.register')}
          </Button>
        </form>

        {/* policy page links */}
        <div className="mb-3 text-mini">
          {locale !== Locale.JAPANESE && t('component:sign_up_card.description.agree')}
          <Link href="/terms-of-service" prefetch={false}>
            <a
              target="_blank"
              rel="noopener"
              className="cursor-pointer text-orange-700 underline underline-offset-2 hover:opacity-60"
            >
              {t('component:sign_up_card.link.terms_of_service')}
            </a>
          </Link>
          {t('component:sign_up_card.description.and')}
          <Link href="/privacy-policy" prefetch={false}>
            <a
              target="_blank"
              rel="noopener"
              className="cursor-pointer text-orange-700 underline underline-offset-2 hover:opacity-60"
            >
              {t('component:sign_up_card.link.privacy_policy')}
            </a>
          </Link>
          {locale === Locale.JAPANESE && t('component:sign_up_card.description.agree')}
        </div>
        {SocialProviderSequence.filter((provider) => provider).length > 0 && (
          <div
            className={classNames('mt-6 w-full', {
              'mx-8': !showInModal,
              'mx-4 sm:mx-8': showInModal,
            })}
          >
            <div
              className={classNames(
                'flex flex-col gap-4 items-center pt-4 pb-8 -mb-8 bg-teal-50 sm:rounded-b-2xl',
                {
                  'px-8 -mx-8': !showInModal,
                  'px-4 sm:px-8 -mx-4 sm:-mx-8 rounded-b-2xl': showInModal,
                }
              )}
            >
              <p className="text-base text-teal-600">
                <b>{t('component:sign_up_card.description.sign_up_with_provider')}</b>
              </p>
              <div className="flex w-full justify-center gap-4">
                {SocialProviderSequence.map(
                  (provider) =>
                    provider && (
                      <Button
                        key={provider}
                        className="flex size-14 items-center !rounded-lg p-3"
                        color="white"
                        onClick={() => events.onClickProviderLogin(provider)}
                      >
                        <Image
                          src={`/social/${provider.toLowerCase()}.svg`}
                          alt={`${provider} Icon`}
                          width={32}
                          height={32}
                          className="cursor-pointer"
                        />
                      </Button>
                    )
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {showModal && (
        <AppleLoginConfirmationModal
          type="signup"
          onContinue={() => events.onClickProviderLogin(CognitoAuthProvider.APPLE)}
          onCancel={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default SignUpCard;
